import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import isNumber from 'lodash/isNumber'
import { theme, SIZES, getPixelsFromSize } from '../../../style'

const BORDER_RADIUS = 5

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: ${({ height }) => ((typeof height === 'string') ? height : `${height}px`)};
  width: ${({ width }) => ((typeof width === 'string') ? width : `${width}px`)};
  background-color: ${({ bgColor }) => theme[bgColor]};
  border: ${({ border }) => (border ? `1px solid ${theme.red}` : null)};
  border-radius: ${({ rounded }) => (rounded ? `${BORDER_RADIUS}px` : 0)};
  align-items: ${({ alignItems }) => alignItems};
  justify-content: ${({ justifyContent }) => justifyContent};
`

/**
 * View component with predefined padding
 * @param {number} height optional height
 * @param {string} p optional padding
 * @param {string} ph optional padding horizontal
 * @param {string} pv optional padding vertical
 * @param {string} pt optional padding top
 * @param {string} pr optional padding right
 * @param {string} pb optional padding bottom
 * @param {string} pl optional padding left
 * @param {string} justifyContent optional justify-content
 * @param {string} alignItems optional align-items
 * @param {string} bgColor optional background color from AppColors
 * @param {object} style optional styles
 * @param {object} rest optional rest props
 */
const Block = ({
    width,
    height,
    bgColor,
    p,
    ph,
    pv,
    pt,
    pr,
    pb,
    pl,
    justifyContent,
    alignItems,
    border,
    rounded,
    style,
    ...rest
}) => {
    // Set default value in case no value is provided
    let phDefault = null
    let pvDefault = null

    if (p == null && ph == null && pv == null && pt == null && pr == null && pb == null && pl == null) {
        phDefault = 'XXXXL'
        pvDefault = 'XXXL'
    }

    const padding = {
        padding: getPixelsFromSize(p),
        paddingTop: getPixelsFromSize(pt || pv || pvDefault),
        paddingRight: getPixelsFromSize(pr || ph || phDefault),
        paddingBottom: getPixelsFromSize(pb || pv || pvDefault),
        paddingLeft: getPixelsFromSize(pl || ph || phDefault),
    }

    // Attach 'px' to all pixel values
    Object.entries(padding).forEach(([key, value]) => {
        if (isNumber(value)) { padding[key] += 'px' }
    })

    return (
        <Container
            width={width}
            height={height}
            bgColor={bgColor}
            border={border}
            rounded={rounded}
            alignItems={alignItems}
            justifyContent={justifyContent}
            style={{
                ...padding,
                ...style,
            }}
            {...rest}
        />
    )
}

Block.propTypes = {
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    height: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    bgColor: PropTypes.oneOf(Object.keys(theme)),
    p: PropTypes.oneOf(Object.values(SIZES)),
    ph: PropTypes.oneOf(Object.values(SIZES)),
    pv: PropTypes.oneOf(Object.values(SIZES)),
    pt: PropTypes.oneOf(Object.values(SIZES)),
    pr: PropTypes.oneOf(Object.values(SIZES)),
    pb: PropTypes.oneOf(Object.values(SIZES)),
    pl: PropTypes.oneOf(Object.values(SIZES)),
    justifyContent: PropTypes.oneOf([
        'center',
        'flex-start',
        'flex-end',
        'space-between',
        'space-around',
        'space-evenly',
    ]),
    alignItems: PropTypes.oneOf([
        'center',
        'flex-start',
        'flex-end',
        'stretch',
    ]),
    border: PropTypes.bool,
    rounded: PropTypes.bool,
    style: PropTypes.object // eslint-disable-line
}

Block.defaultProps = {
    width: 'auto',
    height: 'auto',
    bgColor: 'transparent',
    p: null,
    ph: null,
    pv: null,
    pt: null,
    pr: null,
    pb: null,
    pl: null,
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    border: false,
    rounded: false,
    style: {},
}

export default Block
